import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { UserContext } from "../../../contexts/UserContext";
import { object, setLocale, string, array, bool } from "yup";
import { en } from "yup-locales";
import { useFormik } from "formik";
import {
  _ACTIONS,
  _COLORS,
  _CRUD,
  _HTTP,
  _MESSAGES,
  _SITE_TYPES,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { _ROUTES } from "../../../utils/_routes_constants";
import { RequestHelper } from "../../../utils/requestHelper";
import { BasicListContext } from "../../../contexts/BasicListContext";
import { Box } from "@mui/material";
import { useMaterialReactTable } from "material-react-table";
import BasicModal from "../../helpers/BasicModal";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import { getMuiTableConfig } from "../../../utils/MuiTableUtils/muiTableBuilder";
import SitesListColumns from "../../../utils/MuiTableUtils/ColumnBuilders/SitesListColumns";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import { MuiTableComponent } from "../../helpers/MuiTableComponent";
import { getDataGeneric } from "../../../repository/DataGeneric";
import { createToast } from "../../../utils/toast";
import { buildOptions, getEntityProperties } from "../../../utils/formHelper";

export const SitesList = ({
  editData,
  setEditData,
  showModal,
  setShowModal,
}) => {
  const { open } = useContext(GlobalContext);
  const { user } = useContext(UserContext);
  const [listActivities, setListActivities] = useState([]);
  const v = object({
    name: string().required().label("Name"),
    code: string().required().label("Code for import"),
    activities: array().label("Activities"),
    type: string().label("Type").nullable().notRequired(),
    active: bool().required().label("Active"),
    list: string().label("Knowned name").nullable().notRequired(),
    exportNumber: string().label("Export number").nullable().notRequired(),
  });
  const [listLoaded, setListLoaded] = useState(false);

  const requestHelper = new RequestHelper();

  setLocale(en);

  const {
    rowCount,
    data,
    isLoaded,
    isError,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    isLoading,
    setRefresh,
  } = useContext(BasicListContext);

  /**
   * Processes the given data to create a list of activities and updates the state.
   *
   * @param {Array} data - An array of activity objects.
   * @param {number} data[].id - The unique identifier of the activity.
   * @param {string} data[].name - The name of the activity.
   */
  const actions = (data) => {
    if (data.length > 0) {
      const listActivities = buildOptions(data, "name", "@id");
      setListActivities(listActivities);
    }
  };

  useEffect(() => {
    if (!listLoaded) {
      getDataGeneric(
        requestHelper,
        { "groups[]": ["select:get"], pagination: false, active: 1 },
        actions,
        _ROUTES.ACTIVITIES,
        "Error fetching activities",
      );
      setListLoaded(true);
    }
  }, [listLoaded]);

  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      activities: [],
      type: "",
      active: true,
      list: "",
      exportNumber: "",
    },
    onSubmit: (values) => {
      const methode = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = _ROUTES.SITES + (editData ? `/${editData.id}` : "");

      const activities = values.activities.map((activity) => {
        if (activity instanceof Object) {
          activity = activity["@id"];
        }

        return activity;
      });

      const site = {
        name: values.name,
        code: values.code,
        activities: activities,
        type: values.type,
        active: values.active,
        list: values.list,
        exportNumber: parseInt(values.exportNumber),
      };

      requestHelper
        .doRequest(methode, url, site)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            setShowModal(false);
            setEditData(false);
            setRefresh((prev) => {
              return prev + 1;
            });
          }
        })
        .catch(() => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
    validationSchema: v,
  });

  // Get the table configuration
  const c = getMuiTableConfig(
    // Params
    { enableColumnFilters: true, enableExpanding: true },
    // States & setStates (from context) Have to be the same as this
    {
      rowCount,
      isLoaded,
      isError,
      pagination,
      setPagination,
      columnFilters,
      setColumnFilters,
      globalFilter,
      setGlobalFilter,
      sorting,
      setSorting,
      isLoading,
    },
  );

  const columns = SitesListColumns({
    setEditData,
    setShowModal,
    user,
    setRefresh,
  });

  const table = useMaterialReactTable({
    columns: columns,
    data: data,
    ...c,
    enableColumnResizing: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    initialState: {
      density: "compact",
    },
  });

  useEffect(() => {
    if (editData) {
      formik.setValues({
        name: editData.name,
        code: editData.code,
        activities: getEntityProperties(editData?.activities, "@id"),
        type: editData.type,
        active: editData.active,
        list: editData.list,
        exportNumber: editData.exportNumber,
      });
    } else {
      formik.resetForm();
    }
  }, [editData]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: open ? window.innerWidth - 340 : window.innerWidth - 64,
          borderRadius: "1px",
          boxShadow: `0px 0px 2px ${_COLORS.grey} `,
          overflowX: "auto",
        }}
      >
        {/* Table Component is here */}
        <MuiTableComponent route={_ROUTES.SITES} table={table} />
      </Box>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={editData ? _CRUD.update : _CRUD.create + " Site"}
        content={
          <BasicFormikForm
            formik={formik}
            setEditData={setEditData}
            setShowModal={setShowModal}
            validations={v}
            options={{
              activities: listActivities,
              type: _SITE_TYPES,
            }}
          />
        }
        icon={<CoPresentIcon />}
        width={"500"}
      />
    </>
  );
};
